import { apiConfig } from '../../config/config.bundle';
import type {
  ApiResponse,
  PartyAddress,
  PropertyGroup,
  TruckDetails,
  UserInviteResponse,
} from '../../types/api-types';
import { enhanceApi } from '../../utils/api-utils';
import { getScopedApi } from './base-api';

const userApiConfig = apiConfig.user;

type PropertyGroupsQueryArg = {
  userId: string;
};

type CreatePropertyGroupsMutationArg = {
  body: {
    lastUsedAddress?: boolean;
    // "active": boolean,
    groupName?: string;
    userId: string;
    siteUsedId: string;
  };
};

type UpdatePropertyGroupsMutationArg = {
  prams: {
    lastUsedAddress?: boolean;
    groupName: string;
    userId: string;
    sortOrder: number;
    siteUsedId: number;
  };
};

type UpdateLastUsedAddressMutationArg = {
  params: {
    userId: string;
    lastUsedAddress: boolean;
    sortOrder: number;
  };
};

type GenerateInviteIdMutationArg = {
  userId: string;
  firstName: string;
  lastName: string;
  inviteeEmail: string;
  companyName: string;
  inviterName: string;
  inviterEmail: string;
  selectedProperties:
    | PartyAddress[]
    | {
        ADDRESS1: string;
        CITY: string;
        STATE: string;
        COUNTY: string;
        COUNTRY: string;
        POSTAL_CODE: string;
      }[];
  accountNumber: string[] | number[];
  userRole: string;
  inviterUserId: string;
  addProperty?: boolean;
  isInviteUserForNewAccount?: boolean;
};

type InviteDetailsQueryArg = {
  inviteId: string;
};

type UpdateInviteRequestMutationArg = {
  inviteId: string;
  firstname: string;
  lastname: string;
  phoneNumber?: string;
};

type TruckDetailsQueryArg = {
  siteUseId: string;
};

type SaveTruckDetailsMutationArg = {
  body: {
    siteUseId: string;
    userId: string;
    canAccommodate53ftTruck: boolean;
    hasLoadingDock: boolean;
    needLiftGate: boolean;
  };
};
type DeleteTruckDetailsMutationArg = {
  siteUseId: string;
  userId: string;
};

const userApi = getScopedApi('user').injectEndpoints({
  endpoints: builder => ({
    propertyGroups: builder.query<
      ApiResponse<PropertyGroup[]>,
      PropertyGroupsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: userApiConfig.paths.accounts,
        params: {
          requestType: 'USER_ADDRESS_GET_BY_PK',
          userId: arg.userId.toLowerCase(),
        },
      }),
      providesTags: ['PropertyGroups'],
    }),
    createPropertyGroups: builder.mutation<
      ApiResponse<PropertyGroup>,
      CreatePropertyGroupsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: userApiConfig.paths.accounts,
        body: arg.body,
        params: { requestType: 'USER_ADDRESS_INSERT' },
      }),
      invalidatesTags: ['PropertyGroups'],
    }),
    updatePropertyGroups: builder.mutation<
      ApiResponse<PropertyGroup>,
      UpdatePropertyGroupsMutationArg
    >({
      query: arg => ({
        method: 'PUT',
        url: userApiConfig.paths.accounts,
        body: arg.prams,
        params: {
          requestType: 'USER_ADDRESS_UPDATE',
          userId: arg.prams.userId.toLowerCase(),
          lastUsedAddress: arg.prams.lastUsedAddress,
          groupName: arg.prams.groupName,
          sortOrder: arg.prams.sortOrder,
          siteUsedId: arg.prams.siteUsedId,
        },
      }),
      invalidatesTags: ['PropertyGroups'],
    }),
    updateLastUsedAddress: builder.mutation<
      ApiResponse<unknown>,
      UpdateLastUsedAddressMutationArg
    >({
      query: arg => ({
        method: 'PUT',
        url: userApiConfig.paths.accounts,
        params: {
          requestType: 'USER_ADDRESS_UPDATE_LAST_USED_ADDRESS',
          ...arg.params,
        },
      }),
    }),
    generateInviteId: builder.mutation<
      ApiResponse<UserInviteResponse>,
      GenerateInviteIdMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: userApiConfig.paths.accounts,
        body: arg,
        params: { requestType: 'USER_INVITE_CREATE_REQUEST' },
      }),
    }),
    inviteDetails: builder.query<
      ApiResponse<UserInviteResponse>,
      InviteDetailsQueryArg
    >({
      query: arg => ({
        method: 'POST',
        url: userApiConfig.paths.accounts,
        body: arg,
        params: { requestType: 'USER_INVITE_RESOLVE_REQUEST' },
      }),
    }),
    updateInviteRequest: builder.mutation<
      ApiResponse<UserInviteResponse>,
      UpdateInviteRequestMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: userApiConfig.paths.accounts,
        body: arg,
        params: { requestType: 'USER_INVITE_ACCEPT_REQUEST' },
      }),
    }),
    truckDetails: builder.query<
      ApiResponse<TruckDetails[]>,
      TruckDetailsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: userApiConfig.paths.accounts,
        params: {
          requestType: 'TRUCK_DETAILS_GET_BY_SITE_USE_ID',
          siteUseId: arg.siteUseId,
        },
      }),

      providesTags: (_result, _error, arg) => [
        { type: 'TruckDetails', id: arg.siteUseId },
      ],
    }),
    saveTruckDetails: builder.mutation<
      ApiResponse<TruckDetails>,
      SaveTruckDetailsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: userApiConfig.paths.accounts,
        body: arg.body,
        params: { requestType: 'TRUCK_DETAILS_SAVE' },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'TruckDetails',
          id: arg.body.siteUseId,
        },
      ],
    }),

    deleteTruckDetails: builder.mutation<
      ApiResponse<unknown>,
      DeleteTruckDetailsMutationArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: userApiConfig.paths.accounts,
        params: {
          requestType: 'TRUCK_DETAILS_DELETE',
          siteUseId: arg.siteUseId,
          userId: arg.userId,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'TruckDetails',
          id: arg.siteUseId,
        },
      ],
    }),
  }),
});

export const {
  usePropertyGroupsQuery,
  useLazyPropertyGroupsQuery,
  useCreatePropertyGroupsMutation,
  useUpdatePropertyGroupsMutation,
  useUpdateLastUsedAddressMutation,
  useGenerateInviteIdMutation,
  useInviteDetailsQuery,
  useLazyInviteDetailsQuery,
  useUpdateInviteRequestMutation,
  useTruckDetailsQuery,
  useLazyTruckDetailsQuery,
  useSaveTruckDetailsMutation,
  useDeleteTruckDetailsMutation,
} = enhanceApi(userApi);

export default userApi;
